import { useState } from "react";
import { SelectChangeEvent } from "@mui/material";
import useAffiliateClockStats, {
  AffiliateClockRange,
} from "../../../components/hooks/useAffiliateClockStats";
import { AffiliateClockStat } from "../../../api/admin";
import DSCardContent from "views/system-design/DSCardContent";
import DSBox from "views/system-design/DSBox";
import DSStack from "views/system-design/DSStack";
import DSTypography from "views/system-design/DSTypography";
import DSTextField from "views/system-design/DSTextField";
import DSSelect from "views/system-design/DSSelect";
import DSMenuItem from "views/system-design/DSMenuItem";
import DSButton from "views/system-design/DSButton";
import DSCircularProgress from "views/system-design/DSCircularProgress";
import DSSegmentedTabs from "views/system-design/DSSegmentedTabs";
import {
  DSTable,
  DSTableHead,
  DSTableBody,
  DSTableRow,
  DSTableCell,
} from "views/system-design/Table";

type AnalyticsMode = "marketing" | "studio";

const MODE_OPTIONS: { label: string; value: AnalyticsMode }[] = [
  { label: "Marketing", value: "marketing" },
  { label: "Studio", value: "studio" },
];

const PRESET_DAYS = [7, 30, 90] as const;

const todayStr = () => {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
};

const isCustom = (
  range: AffiliateClockRange,
): range is { from: string; to: string } => "from" in range;

const EDITION_TYPE_LABELS: Record<AffiliateClockStat["type"], string> = {
  artist: "Artist",
  backoffice: "Signature",
};

function AffiliateClockStatsWidget() {
  const [mode, setMode] = useState<AnalyticsMode>("marketing");
  const [range, setRange] = useState<AffiliateClockRange>({ days: 7 });
  const [customMode, setCustomMode] = useState(false);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const { data, isLoading, isError } = useAffiliateClockStats(range);

  const collections = data?.collections ?? [];

  const selectValue =
    customMode || isCustom(range) ? "custom" : String(range.days);

  const handlePresetChange = (e: SelectChangeEvent<unknown>) => {
    const value = e.target.value as string;
    if (value === "custom") {
      setCustomMode(true);
      return;
    }
    setCustomMode(false);
    setRange({ days: Number(value) });
  };

  const canApply = !!from && !!to && from <= to;

  const applyCustomRange = () => {
    if (canApply) {
      setRange({ from, to });
    }
  };

  const numericCellSx = { textAlign: "right" as const, whiteSpace: "nowrap" };

  return (
    <DSBox>
      <DSBox
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 2,
          pt: 2,
          gap: 1,
          flexWrap: "wrap",
        }}
      >
        <DSTypography variant="h6">Artist Edition Performance</DSTypography>
        <DSStack
          direction="row"
          alignItems="center"
          spacing={1}
          flexWrap="wrap"
          justifyContent="flex-end"
        >
          <DSSegmentedTabs
            size="small"
            options={MODE_OPTIONS}
            value={mode}
            onChange={(v) => v && setMode(v)}
          />
          <DSSelect
            size="small"
            value={selectValue}
            onChange={handlePresetChange}
            sx={{ minWidth: 130 }}
          >
            {PRESET_DAYS.map((d) => (
              <DSMenuItem key={d} value={String(d)}>
                Last {d} days
              </DSMenuItem>
            ))}
            <DSMenuItem value="custom">Custom range</DSMenuItem>
          </DSSelect>
          {selectValue === "custom" && (
            <>
              <DSTextField
                type="date"
                size="small"
                label="From"
                InputLabelProps={{ shrink: true }}
                inputProps={{ max: to || todayStr() }}
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                sx={{ width: 150 }}
              />
              <DSTextField
                type="date"
                size="small"
                label="To"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: from, max: todayStr() }}
                value={to}
                onChange={(e) => setTo(e.target.value)}
                sx={{ width: 150 }}
              />
              <DSButton
                size="small"
                variant="contained"
                disabled={!canApply}
                onClick={applyCustomRange}
              >
                Apply
              </DSButton>
            </>
          )}
        </DSStack>
      </DSBox>
      <DSCardContent sx={{ flex: 1, overflowX: "auto" }}>
        {isLoading ? (
          <DSBox sx={{ textAlign: "center", p: "20px" }}>
            <DSCircularProgress />
          </DSBox>
        ) : isError && !data ? (
          <DSTypography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 3 }}
          >
            Failed to load Artist Edition performance
          </DSTypography>
        ) : !collections.length ? (
          <DSTypography
            variant="body2"
            color="text.secondary"
            sx={{ textAlign: "center", py: 3 }}
          >
            No affiliate collections
          </DSTypography>
        ) : (
          <DSTable size="small">
            <DSTableHead>
              <DSTableRow>
                <DSTableCell>Artist</DSTableCell>
                <DSTableCell>Collection</DSTableCell>
                <DSTableCell>Type</DSTableCell>
                {mode === "studio" ? (
                  <>
                    <DSTableCell sx={numericCellSx}>Click to Open</DSTableCell>
                    <DSTableCell sx={numericCellSx}>Click to Copy</DSTableCell>
                    <DSTableCell sx={numericCellSx}>Click to View</DSTableCell>
                  </>
                ) : (
                  <>
                    <DSTableCell sx={numericCellSx}>Product Views</DSTableCell>
                    <DSTableCell sx={numericCellSx}>Purchases</DSTableCell>
                    <DSTableCell sx={numericCellSx}>Conversion</DSTableCell>
                  </>
                )}
              </DSTableRow>
            </DSTableHead>
            <DSTableBody>
              {collections.map((c) => {
                // Prefer the real Shopify PDP URL from the backend; fall back
                // to one derived from the collection name if it's missing.
                // Only the backend knows the real handle: edition handles
                // changed shape when titles dropped "Artist Edition".
                const pdpUrl = c.productUrl ?? null;
                return (
                  <DSTableRow
                    key={c.collectionId}
                    hover
                    onClick={
                      pdpUrl
                        ? () =>
                            window.open(pdpUrl, "_blank", "noopener,noreferrer")
                        : undefined
                    }
                    sx={pdpUrl ? { cursor: "pointer" } : undefined}
                  >
                    <DSTableCell>{c.artistName}</DSTableCell>
                    <DSTableCell>{c.name || "Untitled collection"}</DSTableCell>
                    <DSTableCell>{EDITION_TYPE_LABELS[c.type]}</DSTableCell>
                    {mode === "studio" ? (
                      <>
                        <DSTableCell sx={numericCellSx}>
                          {c.editionOpened}
                        </DSTableCell>
                        <DSTableCell sx={numericCellSx}>
                          {c.editionCopied}
                        </DSTableCell>
                        <DSTableCell sx={numericCellSx}>
                          {c.editionViewed}
                        </DSTableCell>
                      </>
                    ) : (
                      <>
                        <DSTableCell sx={numericCellSx}>
                          {c.productViews}
                        </DSTableCell>
                        <DSTableCell sx={{ ...numericCellSx, fontWeight: 600 }}>
                          {c.purchases}
                        </DSTableCell>
                        <DSTableCell sx={numericCellSx}>
                          {c.conversionRate}%
                        </DSTableCell>
                      </>
                    )}
                  </DSTableRow>
                );
              })}
            </DSTableBody>
          </DSTable>
        )}
      </DSCardContent>
      <DSBox sx={{ px: 2, py: 1.5 }}>
        <DSTypography variant="caption" sx={{ color: "text.secondary" }}>
          {mode === "studio"
            ? "Studio Artist Edition clicks per collection — link opened, copied, and viewed"
            : "Shopify product views and purchases per Artist Edition collection · Conversion = purchases / product views"}
        </DSTypography>
      </DSBox>
    </DSBox>
  );
}

export default AffiliateClockStatsWidget;
